import type { User } from "@common/types/user";
import type { PresenceStackMember } from "@components/avatar/presenceStackOrder";
import { getTaskAssigneeDisplayName } from "../task-assignee.lib";

type TaskPresencePerson = Pick<
    User,
    "id" | "name" | "avatar" | "iconColor" | "email"
>;

/** Who posted in the task's chat; Kick and the Kick agent both show as Kick. */
export type TaskPresenceContributor =
    | { kind: "agent" | "kick" }
    | { kind: "user"; user: TaskPresencePerson };

export interface TaskPresence {
    /** Who else has the task open now. */
    viewers: readonly TaskPresencePerson[];
    /** Always in the stack: null shows the empty "No assignee" slot. */
    assignee: TaskPresencePerson | null;
}

const VIEWING_DETAIL = "Viewing now";
const CONTRIBUTOR_DETAIL = "Contributor";

const withViewing = (detail: string, isViewing: boolean) =>
    isViewing ? `${detail} · ${VIEWING_DETAIL}` : detail;

const getAgentMembers = ({
    isAgentWorking,
    contributors,
}: {
    isAgentWorking: boolean;
    contributors: readonly TaskPresenceContributor[];
}): PresenceStackMember[] => {
    if (isAgentWorking) {
        return [{ kind: "agent", detail: "Working now", isLive: true }];
    }
    return contributors.some(({ kind }) => kind !== "user")
        ? [{ kind: "agent", detail: CONTRIBUTOR_DETAIL, isLive: false }]
        : [];
};

/**
 * A task's presence stack members: the agent while it works on the task (or
 * as a contributor once it posted), who else is viewing it, the people who
 * posted in its chat, then the assignee (or the empty slot). Everyone shows
 * once: an assignee who views or posted as the assignee, a contributor who
 * views as a live contributor, ranked with the viewers.
 */
export const getTaskPresenceMembers = ({
    isAgentWorking,
    viewers,
    assignee,
    contributors = [],
}: TaskPresence & {
    isAgentWorking: boolean;
    contributors?: readonly TaskPresenceContributor[];
}): PresenceStackMember[] => {
    const viewerIds = new Set(viewers.map(({ id }) => id));
    const contributingPeople = contributors.flatMap((contributor) =>
        contributor.kind === "user" && contributor.user.id !== assignee?.id
            ? [contributor.user]
            : [],
    );
    const contributorIds = new Set(contributingPeople.map(({ id }) => id));

    const members = getAgentMembers({ isAgentWorking, contributors });
    for (const viewer of viewers) {
        if (viewer.id !== assignee?.id && !contributorIds.has(viewer.id)) {
            members.push({
                kind: "person",
                id: `viewer-${viewer.id}`,
                name: getTaskAssigneeDisplayName(viewer),
                user: viewer,
                role: "viewer",
                detail: VIEWING_DETAIL,
                isLive: true,
            });
        }
    }
    for (const person of contributingPeople) {
        const isViewing = viewerIds.has(person.id);
        members.push({
            kind: "person",
            id: `contributor-${person.id}`,
            name: getTaskAssigneeDisplayName(person),
            user: person,
            role: isViewing ? "viewer" : "team",
            detail: withViewing(CONTRIBUTOR_DETAIL, isViewing),
            isLive: isViewing,
        });
    }
    if (assignee === null) {
        members.push({ kind: "unassigned" });
    } else {
        const isViewing = viewerIds.has(assignee.id);
        members.push({
            kind: "person",
            id: `assignee-${assignee.id}`,
            name: getTaskAssigneeDisplayName(assignee),
            user: assignee,
            role: "pinned",
            detail: withViewing("Assignee", isViewing),
            isLive: isViewing,
        });
    }
    return members;
};

const isIdleAssigneeSlot = (member: PresenceStackMember) =>
    member.kind === "unassigned" ||
    (member.kind === "person" && member.role === "pinned" && !member.isLive);

/**
 * A List row's presence stack members: the board card's, in its order,
 * without the empty slot or an assignee who isn't viewing, both of which the
 * Assignee column shows. A viewing assignee stays at the right end.
 */
export const getTaskRowPresenceMembers = (
    presence: Parameters<typeof getTaskPresenceMembers>[0],
): PresenceStackMember[] =>
    getTaskPresenceMembers(presence).filter(
        (member) => !isIdleAssigneeSlot(member),
    );
